/**
 * Блок «Жилые дома в аварийном состоянии» (US-23 FR-4) — три плитки:
 * площадь (тыс. кв. м), количество домов (ед.), количество проживающих
 * (человек). Ровно то, что публикует источник, — столбцы B, C и D листа.
 *
 * Плитки ИФО в блоке нет (FR-4.2): индекс по этой таблице источник не
 * публикует, а собственная база сравнения сделала бы показатель решением
 * платформы. Динамика — изменение к предыдущему выпуску с направлением
 * из ответа (US-24 FR-3.7): у площади «рост = ухудшение», поэтому её
 * плитка при росте оформляется предупреждающим оранжевым, а не зелёным
 * ростом (FR-4.4). Направление не выводится из знака изменения.
 *
 * Отчётная подпись блока — дата состояния («на 1 января 2026 года»), а не
 * отчётный год: показатель моментный (FR-4.5, FR-5.3).
 */
import { AlertTriangle, Home, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  PeriodCaption,
  SourceNote,
  StatNoData,
  StatNumber,
  fmt,
  fmt1,
} from './statValue';
import { BlockLoader } from '../../../components/common/BlockLoader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { cn } from '../../../lib/cn';
import type {
  ConstructionEmergencyHousingResponse,
  ConstructionEmergencyMetric,
} from '../../../types/analytics';

interface Props {
  data: ConstructionEmergencyHousingResponse | null;
  loading: boolean;
}

interface TileVM {
  key: string;
  title: string;
  metric: ConstructionEmergencyMetric;
  /** Дробный формат — только у площади; дома и люди целые. */
  format: (v: number) => string;
}

/** Рост показателя, у которого рост объявлен ухудшением (FR-4.4). */
const isBadGrowth = (metric: ConstructionEmergencyMetric): boolean => {
  const change = metric.change;
  if (change === null || change.state !== 'value' || change.value === null) {
    return false;
  }
  return change.direction === 'up_is_bad' && change.value > 0;
};

export const ConstructionEmergencyHousing = ({ data, loading }: Props) => {
  const { t } = useTranslation();

  // Порядок плиток фиксирован; плитка без данных не выводится, остальные
  // равномерно занимают ширину блока (FR-4.3).
  const tiles: TileVM[] = (
    [
      ['area', data?.area ?? null, fmt1],
      ['houses', data?.houses ?? null, fmt],
      ['residents', data?.residents ?? null, fmt],
    ] as Array<[string, ConstructionEmergencyMetric | null, (v: number) => string]>
  ).flatMap(([key, metric, format]) => {
    if (metric === null || metric.value === null) return [];
    if (metric.value.state === 'no_data') return [];
    return [
      {
        key,
        title: t(`analytics.construction.emergencyHousing.${key}`),
        metric,
        format,
      },
    ];
  });

  return (
    <Card
      className="h-full shadow-card"
      data-testid="analytics-construction-emergency"
    >
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-x-4 gap-y-1 space-y-0">
        <div className="flex min-w-0 items-center gap-2.5">
          {/* Знак блока по макету — в предупреждающем, а не зелёном
              квадрате: блок описывает аварийный фонд. */}
          <span
            className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-status-warning/10 text-status-warning"
            aria-hidden="true"
          >
            <Home className="size-4" strokeWidth={1.8} />
          </span>
          <CardTitle className="min-w-0 text-base text-text">
            {t('analytics.construction.emergencyHousing.title')}
          </CardTitle>
        </div>
        {/* Подпись — дата состояния, а не отчётный год (FR-4.5). */}
        <PeriodCaption
          period={data?.period ?? null}
          testId="construction-emergency-period"
        />
      </CardHeader>
      <CardContent>
        {loading ? (
          <BlockLoader />
        ) : tiles.length === 0 ? (
          <StatNoData text={t('common.noData')} />
        ) : (
          <>
            {/* Три плитки в ряд (макет). Блок стоит в трети ширины
                строки, и на 1280 под него остаётся ~290 px, поэтому не
                сетка из трёх колонок (плитка вышла бы по 80 px), а
                flex-wrap с min-w-32: где хватает ширины — ряд из трёх,
                где нет — перенос на две и одну. */}
            <section
              className="flex flex-wrap gap-2.5"
              data-testid="block-construction-emergency-housing"
            >
              {tiles.map((tile) => {
                const warn = isBadGrowth(tile.metric);
                return (
                  <div
                    key={tile.key}
                    data-testid={`construction-emergency-${tile.key}`}
                    className={cn(
                      'flex min-w-32 flex-1 flex-col rounded-2xl border p-4',
                      warn
                        ? 'border-status-warning/40 bg-status-warning/10'
                        : 'border-surface-border bg-surface-page',
                    )}
                  >
                    <h3 className="min-w-0 text-sm font-semibold leading-snug text-text-muted">
                      {tile.title}
                    </h3>
                    {/* Подписи переносятся на две-три строки, поэтому
                        значения прижаты к низу плитки (§Дизайн US-23). */}
                    <p className="mt-auto flex flex-wrap items-baseline gap-x-2 pt-3">
                      <StatNumber
                        value={tile.metric.value}
                        format={tile.format}
                        className={cn(
                          // Значения блока тёмные (макет): индиго плашки
                          // остаётся признаком ключевых величин раздела.
                          'text-3xl leading-tight',
                          warn ? 'text-status-warning' : 'text-text',
                        )}
                        testId={`construction-emergency-${tile.key}-value`}
                      />
                      {tile.metric.value?.unit != null && (
                        <span className="text-sm text-text-muted">
                          {tile.metric.value.unit}
                        </span>
                      )}
                    </p>
                    {warn && (
                      <p
                        className="mt-1 flex items-center gap-1.5 text-[13px] font-semibold text-status-warning"
                        data-testid={`construction-emergency-${tile.key}-warning`}
                        title={t(
                          'analytics.construction.emergencyHousing.growthHint',
                        )}
                      >
                        <AlertTriangle
                          className="size-3.5 shrink-0"
                          aria-hidden="true"
                        />
                        {[
                          tile.metric.change?.value != null
                            ? `+${fmt1(tile.metric.change.value)}%`
                            : null,
                          tile.metric.change?.base_label ?? null,
                        ]
                          .filter((part): part is string => part !== null)
                          .join(' ')}
                      </p>
                    )}
                  </div>
                );
              })}
            </section>

            {/* Сноска о трактовке роста (макет): строка со знаком «i». */}
            <p className="mt-3 flex items-start gap-1.5 text-xs leading-snug text-text-faint">
              <Info
                className="mt-px size-3.5 shrink-0"
                strokeWidth={1.8}
                aria-hidden="true"
              />
              <span className="min-w-0">
                {t('analytics.construction.emergencyHousing.note')}
              </span>
            </p>
            <SourceNote source={data?.source ?? null} />
          </>
        )}
      </CardContent>
    </Card>
  );
};
